// AI管理コンソール — 左サイドバー構成・プロバイダー中心の分かりやすい設計
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Server, Cpu, Route, Database, BarChart3, PlayCircle, Cloud, HardDrive, Zap, Globe, BookOpenText, Activity, ShieldCheck, FileText, Languages, ScrollText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth, canViewAiLogs } from '@/auth/AuthContext';
import AiProviderCard from '../AiProviderCard';
import { ModelsSection, RoutingSection } from './consoleTabs';
import { RagSection, AnalyticsSection, TestSection, WebSearchSection, PromptsSection } from './consoleTabs2';
import AiJobsSection from './AiJobsSection';
import StabilitySection from './StabilitySection';
import ReliabilitySection from './ReliabilitySection';
import TermGuardSection from './TermGuardSection';
import DocGenSection from './DocGenSection';
import TranslateSection from './TranslateSection';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api/ai/console`;

const NAV = [
  { id: 'general', label: '概要と接続', Icon: Server, desc: 'AIの実行先と接続設定' },
  { id: 'models', label: 'モデル管理', Icon: Cpu, desc: '検出されたモデル一覧' },
  { id: 'routing', label: '機能ルーティング', Icon: Route, desc: '機能ごとのモデル割り当て' },
  { id: 'jobs', label: 'ジョブ管理', Icon: Activity, desc: '実行中のAIジョブを中止' },
  { id: 'rag', label: 'RAG設定', Icon: Database, desc: 'ナレッジ検索の索引' },
  { id: 'prompts', label: 'プロンプトと用語集', Icon: BookOpenText, desc: '会社固有の指示と用語定義' },
  { id: 'termguard', label: '社内用語ガード', Icon: ShieldCheck, desc: '全AI機能で用語を自動正規化' },
  { id: 'translate', label: '翻訳（日→英）', Icon: Languages, desc: '英語表示の状態と用語の固定' },
  { id: 'websearch', label: 'Web検索', Icon: Globe, desc: 'アシスタントのインターネット検索' },
  { id: 'docgen', label: '文書生成', Icon: FileText, desc: 'SEN249 PDF/DOCX生成サービス' },
  { id: 'stability', label: 'OCR安定化と診断', Icon: Activity,
    desc: '画像リクエストの同時実行と失敗の内訳' },
  { id: 'reliability', label: '稼働構成と信頼性', Icon: HardDrive,
    desc: '稼働プロセスの実測と有効化フラグ' },
  { id: 'analytics', label: '利用状況', Icon: BarChart3, desc: 'トークン・速度・エラー' },
  { id: 'ai-ops', label: 'AI実行ログ', Icon: ScrollText, desc: '実行ごとのモデル・解析・回復・警告（メタデータのみ）', to: '/settings/ai-operations', adminOnly: true },
  { id: 'test', label: 'テスト', Icon: PlayCircle, desc: 'モデルの動作確認' },
];

export default function AiConsole() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [models, setModels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState('general');

  const load = useCallback(() => {
    axios.get(`${API}/overview`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => setData(null));
    axios.get(`${API}/models`, { withCredentials: true }).then((r) => setModels(r.data.models || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Beta-0.5B versioned writer: every save must carry the version it was based
  // on. On 409 the stored version has moved (another admin, or a background
  // migration) — retry once against the version the server just reported so a
  // single admin never has to save twice.
  const save = async (partial) => {
    setSaving(true);
    const put = async (version) => {
      const r = await axios.put(`${API}/settings`,
        { ...partial, expected_version: version ?? null }, { withCredentials: true });
      setData((d) => ({ ...d, settings: r.data.settings, version: r.data.version }));
      toast.success('保存しました');
      return true;
    };
    try {
      return await put(data?.version);
    } catch (e) {
      if (e?.response?.status === 409) {
        try {
          return await put(e.response.data?.detail_obj?.current_version ?? null);
        } catch (e2) {
          toast.error(e2?.response?.data?.detail || '保存に失敗しました');
          load();
          return false;
        }
      }
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
      return false;
    } finally { setSaving(false); }
  };

  if (user?.role !== 'admin') {
    return <p className="p-8 text-sm text-zinc-500" data-testid="ai-console-denied">このページは管理者のみ利用できます</p>;
  }
  if (!data) return <p className="p-8 text-sm text-zinc-400">読み込み中...</p>;

  const isCustom = data.settings.mode === 'custom';
  const props = { features: data.features, settings: data.settings, models, save, saving, isCustom, onModels: setModels };

  return (
    <div className="space-y-5" data-testid="ai-console">
      {/* ── プロバイダーヒーロー: 今どこでAIが動いているか一目で ── */}
      <div className={`rounded-lg border p-5 sm:p-6 ${isCustom
        ? 'border-indigo-200 bg-zinc-50'
        : 'border-emerald-200 bg-zinc-50'}`}
        data-testid="ai-console-hero">
        <div className="flex flex-wrap items-center gap-4">
          <div className={`rounded-xl p-3 text-white shadow-sm ${isCustom ? 'bg-indigo-600' : 'bg-emerald-600'}`}>
            {isCustom ? <HardDrive size={26} strokeWidth={1.5} /> : <Cloud size={26} strokeWidth={1.5} />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">現在のAI実行先</p>
            <h1 className="text-xl font-semibold tracking-tight text-zinc-900">
              {isCustom ? 'ローカルLLM（自社サーバー）' : 'Emergent クラウド（既定）'}
            </h1>
            <p className="mt-0.5 text-xs text-zinc-500">
              {isCustom
                ? `全AI機能が ${data.settings.base_url || '未設定'} 上のモデルで動作しています`
                : 'すべてのAI機能がEmergentのクラウドAIで動作しています。切り替えは「概要と接続」から行えます'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${isCustom ? 'bg-indigo-400' : 'bg-emerald-400'}`} />
              <span className={`relative inline-flex h-3 w-3 rounded-full ${isCustom ? 'bg-indigo-500' : 'bg-emerald-500'}`} />
            </span>
            <span className="text-xs font-bold text-zinc-600" data-testid="ai-console-hero-status">稼働中</span>
          </div>
        </div>
        {isCustom && (
          <div className="mt-4 flex flex-wrap gap-2">
            {[[`モデル ${models.filter((m) => m.online !== false).length} 件検出`, Cpu],
              [`ルーティング ${Object.keys(data.settings.routing || {}).length} 機能`, Route],
              [data.settings.rag?.enabled ? 'RAG 有効' : 'RAG 無効', Database]].map(([t, I], i) => (
              <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-white bg-white/70 px-3 py-1 text-[11px] font-semibold text-zinc-600 backdrop-blur">
                <I size={12} strokeWidth={1.5} /> {t}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-5 lg:flex-row">
        {/* ── 左ナビ ── */}
        <nav className="lg:w-60 shrink-0 space-y-1" data-testid="ai-console-nav">
          {NAV.filter((n) => !n.adminOnly || canViewAiLogs(user)).map(({ id, label, Icon, desc, to }) => (
            <button key={id} type="button" onClick={() => (to ? navigate(to) : setSection(id))}
              data-testid={`ai-console-tab-${id}`}
              className={`flex w-full items-start gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-all duration-200 ease-out ${
                section === id
                  ? 'border-indigo-200 bg-indigo-50/70 shadow-sm'
                  : 'border-transparent hover:border-zinc-200 hover:bg-white'}`}>
              <Icon size={17} strokeWidth={1.5}
                className={`mt-0.5 shrink-0 ${section === id ? 'text-indigo-600' : 'text-zinc-400'}`} />
              <span className="min-w-0">
                <span className={`block text-[13px] font-semibold ${section === id ? 'text-indigo-800' : 'text-zinc-700'}`}>{label}</span>
                <span className="block truncate text-[10px] text-zinc-400">{desc}</span>
              </span>
            </button>
          ))}
          <div className="mt-3 rounded-xl border border-zinc-200/60 bg-white p-3.5">
            <p className="flex items-center gap-1.5 text-[11px] font-bold text-zinc-600"><Zap size={12} className="text-amber-500" /> はじめての方へ</p>
            <ol className="mt-1.5 list-decimal space-y-0.5 pl-4 text-[10.5px] leading-relaxed text-zinc-500">
              <li>「概要と接続」でローカルLLMのURLを設定</li>
              <li>「モデル管理」で再検出</li>
              <li>「機能ルーティング」で機能ごとにモデルを選択</li>
              <li>「テスト」で動作確認</li>
            </ol>
          </div>
        </nav>

        {/* ── メイン ── */}
        <div className="min-w-0 flex-1 max-w-5xl">
          {section === 'general' && <AiProviderCard models={models} onModels={setModels} />}
          {section === 'models' && <ModelsSection {...props} />}
          {section === 'routing' && <RoutingSection {...props} />}
          {section === 'jobs' && <AiJobsSection />}
          {section === 'docgen' && <DocGenSection />}
          {section === 'rag' && <RagSection {...props} />}
          {section === 'prompts' && <PromptsSection {...props} />}
          {section === 'termguard' && <TermGuardSection />}
          {section === 'translate' && <TranslateSection />}
          {section === 'websearch' && <WebSearchSection {...props} />}
          {section === 'stability' && <StabilitySection />}
          {section === 'reliability' && <ReliabilitySection />}
          {section === 'analytics' && <AnalyticsSection />}
          {section === 'test' && <TestSection models={models} isCustom={isCustom} />}
        </div>
      </div>
    </div>
  );
}
